import {type AppError, InvalidTokenError, type Result} from '@packages/utils';
import {type UseQueryOptions, useQuery} from '@tanstack/react-query';
import {parseCookies} from 'nookies';
import type {z} from 'zod';
import {DEFAULT_API_VERSION} from '../../config/apiVersion';
import {defaultClient} from '../../lib/defaultClient';
import {GeneralCarouselSchema} from '../entities/generalCarousel/schema';
import {sendApiRequest} from '../usecases/auth/sendApiRequest';
import {useIsSessionExpired} from '../usecases/auth/useIsSessionExpired';

export type GeneralCarouselResponse = z.infer<typeof GeneralCarouselSchema>;
export type ResultGeneralCarousel = Result<GeneralCarouselResponse, AppError>;

type GetGeneralCarouselArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
};

export const getGeneralCarousel = async ({
  cookies,
  version = DEFAULT_API_VERSION,
}: GetGeneralCarouselArgs): Promise<ResultGeneralCarousel> => {
  const request = async (
    accessToken?: string,
  ): Promise<Result<GeneralCarouselResponse, AppError>> => {
    if (accessToken) {
      defaultClient.setToken(accessToken);
    }

    return defaultClient.get(
      `/${version}/feed/general-carousel`,
      GeneralCarouselSchema,
    );
  };

  const result = await sendApiRequest<GeneralCarouselResponse>({
    cookies,
    authRequired: false,
    request,
    version,
  });

  return result;
};

type UseGetGeneralCarouselOptions = {
  config?: UseQueryOptions<ResultGeneralCarousel>;
  version?: string;
};

export const useGetGeneralCarousel = ({
  config,
  version,
}: UseGetGeneralCarouselOptions = {}) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useQuery<ResultGeneralCarousel>({
    ...config,
    queryKey: ['generalCarousel'],
    async queryFn() {
      const cookies = parseCookies();
      const result = await getGeneralCarousel({cookies, version});

      if (!result?.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};
